import { isEqual } from 'lodash';
import type {
  Geometry as MartinezGeometry,
  MultiPolygon as MartinezMultiPolygon,
  Polygon as MartinezPolygon,
  Ring as MartinezRing,
} from 'martinez-polygon-clipping';
import { roundToDecimals } from '../math.helpers';

/**
 * Martinez clip operation (union / diff / intersection / xor).
 */
export type MartinezClipOp = (
  a: MartinezGeometry,
  b: MartinezGeometry,
) => MartinezGeometry | null;

/**
 * Millimetre snap for planar coords. Float noise between "identical" floors
 * otherwise feeds almost-coincident edges into Martinez and can hang the
 * event loop indefinitely (sync, so timeouts never fire).
 */
export const MARTINEZ_COORD_DECIMALS = 3;

export type MartinezClipKind = 'union' | 'intersection' | 'diff' | 'xor';

type Point = [number, number];

/**
 * True when {@link MartinezGeometry} is a single Polygon (not MultiPolygon).
 */
const isMartinezPolygonGeometry = (
  geometry: MartinezGeometry,
): geometry is MartinezPolygon => {
  if (!geometry.length) return false;
  const firstRing = geometry[0];
  if (!firstRing?.length) return false;
  const firstOfRing = firstRing[0];
  if (!Array.isArray(firstOfRing)) return false;
  return typeof firstOfRing[0] === 'number';
};

/**
 * Normalizes Polygon vs MultiPolygon so equality checks compare the same shape.
 */
export const toMartinezMultiPolygon = (
  geometry: MartinezGeometry,
): MartinezMultiPolygon =>
  isMartinezPolygonGeometry(geometry) ? [geometry] : geometry;

/**
 * Snaps every coordinate in a Martinez geometry to {@link decimals} places.
 */
export const snapMartinezGeometry = (
  geometry: MartinezGeometry,
  decimals: number = MARTINEZ_COORD_DECIMALS,
): MartinezGeometry => {
  const snapPoint = ([x, y]: Point): Point => [
    roundToDecimals(x, decimals),
    roundToDecimals(y, decimals),
  ];
  const snapRing = (ring: MartinezRing): MartinezRing =>
    ring.map((point) => snapPoint(point as Point));
  const snapPolygon = (polygon: MartinezPolygon): MartinezPolygon =>
    polygon.map(snapRing);

  if (isMartinezPolygonGeometry(geometry)) return snapPolygon(geometry);
  return geometry.map(snapPolygon);
};

/**
 * True when geometries are equal after snap + Polygon/MultiPolygon normalize.
 */
export const areMartinezGeometriesEqualAfterSnap = (
  a: MartinezGeometry,
  b: MartinezGeometry,
  decimals: number = MARTINEZ_COORD_DECIMALS,
): boolean =>
  isEqual(
    toMartinezMultiPolygon(snapMartinezGeometry(a, decimals)),
    toMartinezMultiPolygon(snapMartinezGeometry(b, decimals)),
  );

/**
 * Open-ring length (drops duplicate closing vertex when present).
 */
const openRingLength = (ring: MartinezRing): number => {
  if (
    ring.length > 1 &&
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1]
  ) {
    return ring.length - 1;
  }
  return ring.length;
};

/**
 * Orientation for segment predicates (0 colinear, 1/2 clockwise-ish).
 */
const orientation = (a: Point, b: Point, c: Point, epsilon = 1e-9): number => {
  const value = (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
  if (Math.abs(value) <= epsilon) return 0;
  return value > 0 ? 1 : 2;
};

/**
 * True when {@link p} lies on segment ab (inclusive, with epsilon).
 */
const isPointOnSegment = (
  p: Point,
  a: Point,
  b: Point,
  epsilon = 1e-9,
): boolean => {
  const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
  if (Math.abs(cross) > epsilon) return false;
  const minX = Math.min(a[0], b[0]) - epsilon;
  const maxX = Math.max(a[0], b[0]) + epsilon;
  const minY = Math.min(a[1], b[1]) - epsilon;
  const maxY = Math.max(a[1], b[1]) + epsilon;
  return p[0] >= minX && p[0] <= maxX && p[1] >= minY && p[1] <= maxY;
};

/**
 * Segments intersect including collinear overlap and T-junctions.
 */
const segmentsIntersect = (
  p1: Point,
  p2: Point,
  q1: Point,
  q2: Point,
): boolean => {
  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && isPointOnSegment(q1, p1, p2)) return true;
  if (o2 === 0 && isPointOnSegment(q2, p1, p2)) return true;
  if (o3 === 0 && isPointOnSegment(p1, q1, q2)) return true;
  if (o4 === 0 && isPointOnSegment(p2, q1, q2)) return true;
  return false;
};

/**
 * Proper X-crossing only (excludes collinear overlap and endpoint touches).
 */
const segmentsCrossProperly = (
  p1: Point,
  p2: Point,
  q1: Point,
  q2: Point,
): boolean => {
  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
};

/**
 * Length of collinear overlap between two segments (0 when none).
 */
const collinearOverlapLength = (
  a1: Point,
  a2: Point,
  b1: Point,
  b2: Point,
  epsilon = 1e-9,
): number => {
  const ax = a2[0] - a1[0];
  const ay = a2[1] - a1[1];
  const aLen = Math.hypot(ax, ay);
  if (aLen <= epsilon) return 0;
  const bx = b2[0] - b1[0];
  const by = b2[1] - b1[1];
  if (Math.hypot(bx, by) <= epsilon) return 0;
  const perpDist = (p: Point) =>
    Math.abs(ax * (p[1] - a1[1]) - ay * (p[0] - a1[0])) / aLen;
  if (perpDist(b1) > epsilon || perpDist(b2) > epsilon) return 0;
  const projectT = (p: Point) =>
    ((p[0] - a1[0]) * ax + (p[1] - a1[1]) * ay) / (aLen * aLen);
  const tMin = Math.max(0, Math.min(projectT(b1), projectT(b2)));
  const tMax = Math.min(1, Math.max(projectT(b1), projectT(b2)));
  const overlapLen = (tMax - tMin) * aLen;
  return overlapLen > epsilon ? overlapLen : 0;
};

/**
 * True when a closed ring self-intersects (bowtie etc.).
 */
export const isMartinezRingSelfIntersecting = (ring: MartinezRing): boolean => {
  const len = openRingLength(ring);
  if (len < 3) return false;
  for (let i = 0; i < len; i += 1) {
    const p1 = ring[i] as Point | undefined;
    const p2 = ring[(i + 1) % len] as Point | undefined;
    if (!p1 || !p2) continue;
    for (let j = i + 2; j < len; j += 1) {
      if (i === 0 && j === len - 1) continue;
      const q1 = ring[j] as Point | undefined;
      const q2 = ring[(j + 1) % len] as Point | undefined;
      if (!q1 || !q2) continue;
      if (segmentsIntersect(p1, p2, q1, q2)) return true;
    }
  }
  return false;
};

/**
 * Strict interior point-in-polygon (boundary counts as outside).
 */
const isPointStrictlyInRing = (point: Point, ring: MartinezRing): boolean => {
  const len = openRingLength(ring);
  if (len < 3) return false;
  for (let i = 0, j = len - 1; i < len; j = i, i += 1) {
    const a = ring[j] as Point | undefined;
    const b = ring[i] as Point | undefined;
    if (!a || !b) continue;
    if (isPointOnSegment(point, a, b)) return false;
  }
  let inside = false;
  const [px, py] = point;
  for (let i = 0, j = len - 1; i < len; j = i, i += 1) {
    const a = ring[j] as Point | undefined;
    const b = ring[i] as Point | undefined;
    if (!a || !b) continue;
    const [xi, yi] = a;
    const [xj, yj] = b;
    const intersects =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

/**
 * True when rings share a party-wall (collinear overlap) without interior overlap
 * or a proper X-crossing — the hang-prone adjacent case.
 */
export const martinezRingsShareBoundaryOnly = (
  a: MartinezRing,
  b: MartinezRing,
): boolean => {
  const aLen = openRingLength(a);
  const bLen = openRingLength(b);
  let shared = 0;
  let properCross = false;
  for (let i = 0; i < aLen; i += 1) {
    const a0 = a[i] as Point | undefined;
    const a1 = a[(i + 1) % aLen] as Point | undefined;
    if (!a0 || !a1) continue;
    for (let j = 0; j < bLen; j += 1) {
      const b0 = b[j] as Point | undefined;
      const b1 = b[(j + 1) % bLen] as Point | undefined;
      if (!b0 || !b1) continue;
      if (segmentsCrossProperly(a0, a1, b0, b1)) properCross = true;
      shared += collinearOverlapLength(a0, a1, b0, b1);
    }
  }
  if (shared <= 0 || properCross) return false;
  // Interior overlap (e.g. L ∩ rectangle) must still reach Martinez — check
  // vertices and edge midpoints (vertices alone often sit on the shared wall).
  const samplesInside = (ring: MartinezRing, other: MartinezRing): boolean => {
    const len = openRingLength(ring);
    for (let i = 0; i < len; i += 1) {
      const p = ring[i] as Point | undefined;
      if (p && isPointStrictlyInRing(p, other)) return true;
      const q = ring[(i + 1) % len] as Point | undefined;
      if (!p || !q) continue;
      const mid: Point = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
      if (isPointStrictlyInRing(mid, other)) return true;
    }
    return false;
  };
  if (samplesInside(a, b) || samplesInside(b, a)) return false;
  return true;
};

/**
 * True when any ring pair shares/crosses edges (extrusion-style diff guard).
 */
export const martinezRingsShareOrCrossEdges = (
  a: MartinezRing,
  b: MartinezRing,
): boolean => {
  const aLen = openRingLength(a);
  const bLen = openRingLength(b);
  for (let i = 0; i < aLen; i += 1) {
    const a0 = a[i] as Point | undefined;
    const a1 = a[(i + 1) % aLen] as Point | undefined;
    if (!a0 || !a1) continue;
    for (let j = 0; j < bLen; j += 1) {
      const b0 = b[j] as Point | undefined;
      const b1 = b[(j + 1) % bLen] as Point | undefined;
      if (!b0 || !b1) continue;
      if (segmentsIntersect(a0, a1, b0, b1)) return true;
    }
  }
  return false;
};

/**
 * Stable key for a snapped polygon (outer + holes) for ring-level dedupe.
 */
const polygonDedupeKey = (polygon: MartinezPolygon): string =>
  JSON.stringify(polygon);

/**
 * Result when snapped inputs are equal — avoid calling Martinez at all.
 */
const shortCircuitEqualInputs = (
  op: MartinezClipKind,
  snappedA: MartinezGeometry,
): MartinezGeometry | null => {
  switch (op) {
    case 'union':
    case 'intersection':
      return snappedA;
    case 'diff':
    case 'xor':
      return null;
  }
};

/**
 * Fallback when Martinez must not run on degenerate inputs.
 */
const fallbackWithoutMartinez = (
  op: MartinezClipKind,
  aMulti: MartinezMultiPolygon,
  bMulti: MartinezMultiPolygon,
): MartinezGeometry | null => {
  switch (op) {
    case 'union':
      // Keep both footprints without dissolving shared edges.
      return [...aMulti, ...bMulti];
    case 'intersection':
      return null;
    case 'diff':
      // Refuse unsafe subtraction — keep subject.
      return aMulti.length === 1 ? aMulti[0]! : aMulti;
    case 'xor':
      return [...aMulti, ...bMulti];
  }
};

/**
 * True when any ring in the geometries is self-intersecting.
 */
const hasSelfIntersectingRing = (multi: MartinezMultiPolygon): boolean =>
  multi.some((polygon) => polygon.some(isMartinezRingSelfIntersecting));

/**
 * True when any outer ring of A shares a party wall with any outer ring of B.
 */
const multisShareBoundaryOnly = (
  aMulti: MartinezMultiPolygon,
  bMulti: MartinezMultiPolygon,
): boolean => {
  for (const aPoly of aMulti) {
    const aOuter = aPoly[0];
    if (!aOuter) continue;
    for (const bPoly of bMulti) {
      const bOuter = bPoly[0];
      if (!bOuter) continue;
      if (martinezRingsShareBoundaryOnly(aOuter, bOuter)) return true;
    }
  }
  return false;
};

/**
 * True when any ring of A shares or crosses any ring of B (diff-style guard).
 */
const multisShareOrCrossEdges = (
  aMulti: MartinezMultiPolygon,
  bMulti: MartinezMultiPolygon,
): boolean => {
  for (const aPoly of aMulti) {
    for (const aRing of aPoly) {
      for (const bPoly of bMulti) {
        for (const bRing of bPoly) {
          if (martinezRingsShareOrCrossEdges(aRing, bRing)) return true;
        }
      }
    }
  }
  return false;
};

/**
 * Drops polygons from B whose snapped shape already exists in A (union only).
 */
const filterAlreadyIncludedPolygons = (
  aMulti: MartinezMultiPolygon,
  bMulti: MartinezMultiPolygon,
): MartinezMultiPolygon => {
  const included = new Set(aMulti.map(polygonDedupeKey));
  return bMulti.filter((polygon) => !included.has(polygonDedupeKey(polygon)));
};

/**
 * Rebuilds Geometry: single Polygon when one poly, else MultiPolygon.
 */
const toGeometry = (multi: MartinezMultiPolygon): MartinezGeometry =>
  multi.length === 1 ? multi[0]! : multi;

type RunSafeMartinezOptions = {
  decimals?: number;
  /** Override the underlying op (tests). Defaults to {@link rawOp}. */
  invoke?: MartinezClipOp;
};

/**
 * Runs a Martinez clip after snapping coords and skipping hang-prone inputs:
 * - full geometries equal after snap
 * - union: B polygons already present in A (ring-level float-noise)
 * - self-intersecting rings
 * - union / intersection / xor: party-wall shared boundary only (no interior overlap)
 * - diff: any shared or crossing edges (extrusion-style)
 *
 * Does not import `martinez-polygon-clipping` — pass the raw op in. The package
 * CJS build throws `default is not a constructor` under webpack/Node require.
 * Server code must `await import('martinez-polygon-clipping')` (ESM) instead.
 */
export const runSafeMartinez = (
  op: MartinezClipKind,
  rawOp: MartinezClipOp,
  a: MartinezGeometry,
  b: MartinezGeometry,
  options: RunSafeMartinezOptions = {},
): MartinezGeometry | null => {
  const decimals = options.decimals ?? MARTINEZ_COORD_DECIMALS;
  const invoke = options.invoke ?? rawOp;
  const snappedA = snapMartinezGeometry(a, decimals);
  const snappedB = snapMartinezGeometry(b, decimals);
  const aMulti = toMartinezMultiPolygon(snappedA);
  let bMulti = toMartinezMultiPolygon(snappedB);

  if (isEqual(aMulti, bMulti)) {
    return shortCircuitEqualInputs(op, snappedA);
  }

  if (op === 'union') {
    bMulti = filterAlreadyIncludedPolygons(aMulti, bMulti);
    if (bMulti.length === 0) return snappedA;
  }

  if (hasSelfIntersectingRing(aMulti) || hasSelfIntersectingRing(bMulti)) {
    return fallbackWithoutMartinez(op, aMulti, bMulti);
  }

  // Party walls (collinear overlap, no proper X) hang Martinez; keep both / empty.
  if (
    (op === 'union' || op === 'intersection' || op === 'xor') &&
    multisShareBoundaryOnly(aMulti, bMulti)
  ) {
    return fallbackWithoutMartinez(op, aMulti, bMulti);
  }

  // Diff: same conservative guard as extrusion (shared or crossing edges).
  if (op === 'diff' && multisShareOrCrossEdges(aMulti, bMulti)) {
    return fallbackWithoutMartinez(op, aMulti, bMulti);
  }

  return invoke(toGeometry(aMulti), toGeometry(bMulti));
};

/**
 * Builds a float-noise-safe clip op around a raw Martinez function.
 */
export const createSafeMartinezClipOp = (
  op: MartinezClipKind,
  rawOp: MartinezClipOp,
): MartinezClipOp => {
  return (a, b) => runSafeMartinez(op, rawOp, a, b);
};
